// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.ConnectionStringBuilder.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//
// </copyright>
// ----------------------------------------------------------------------

/**
 * Connection String Builder component
 */
import 'share/utils/Izenda.StringUtilities';
import React, { Component } from 'react';
import { observable } from 'mobx';
import PropTypes from 'prop-types';
import connect from 'share/enhancers/Izenda.Connect';

import DropDown from 'share/components/controls/Izenda.DropDown';
import Popup from 'share/components/controls/CommonPopup/Izenda.Popup';
import constants from 'logic/data/Izenda.ConnectionStringBuilderConstants';
import Validator from 'share/components/hoc/Izenda.Validator';
import Input from 'share/components/controls/Izenda.Input';

const InputValidator = Validator()(Input);
@connect()
export default class ConnectionStringBuilder extends Component {
  @observable serverName: String = '';
  @observable database: String = '';
  @observable authentication: String ='';
  @observable login: String = '';
  @observable password: String = '';
  @observable options: String = '';

  static propTypes = {
    serverName: PropTypes.string,
    database: PropTypes.string,
    login: PropTypes.string,
    password: PropTypes.string,
    options: PropTypes.string,
    onOk: PropTypes.func.isRequired
  };

  static defaultProps = {
    serverName: '',
    database: '',
    login: '',
    password: '',
    options: '',
    modalId: 'myModal'
  };

  constructor(props) {
    super(props);
    const connectionInfo = props.connectionInfo || {};
    this.serverName = connectionInfo.serverName || '';
    this.database = connectionInfo.database || '';
    this.authentication = constants.AUTHENTICATION_TYPES['Standard'].value || '';
    this.login = connectionInfo.login || '';
    this.password = connectionInfo.password || '';
    this.options = connectionInfo.options || '';
  }

  /**
   * Handle onChange for ServerName
   * @param {Object} event
   */
  handleServerNameInput = (e: Object) => {
    this.serverName = e.target.value;
  };

  /**
   * Handle onChange for Database
   * @param {Object} event
   */
  handleDatabaseInput = (e: Object) => {
    this.database = e.target.value;
  };

  /**
   * Handle onChange for Authentication
   * @param {Object} event
   */
  handleAuthenticationInput = (e: Object) => {
    this.authentication = e.value;
  };

  /**
   * Handle onChange for Login
   * @param {Object} event
   */
  handleLoginInput = (e: Object) => {
    this.login = e.target.value;
  };

  /**
   * Handle onChange for Password
   * @param {Object} event
   */
  handlePasswordInput = (e: Object) => {
    this.password = e.target.value;
  };

  /**
   * Handle onChange for Options
   * @param {Object} event
   */
  handleOptionsInput = (e: Object) => {
    this.options = e.target.value;
  };

  /**
   * Build options for the buildAuthenticationOptions element
   * @returns {Object}
   */
  buildAuthenticationOptions() {
    const serverType = this.props.serverType || '';
    const { AUTHENTICATION_TYPES } = constants;
    const options = [
      {
        value: AUTHENTICATION_TYPES['Standard'].value,
        label: String.format(AUTHENTICATION_TYPES['Standard'].displayPattern, serverType)
      },
      {
        value: AUTHENTICATION_TYPES['Trusted'].value,
        label: AUTHENTICATION_TYPES['Trusted'].displayPattern
      }
    ];

    return (
      <DropDown
        id='connectionStringBuilder_Authentication'
        options={options}
        value={this.authentication}
        labelField='label'
        valueField='value'
        onChange={this.handleAuthenticationInput}
      />
    );
  }

  /**
   * Compose connection string
   */
  composeConnectionString() {
    const { password, login, database, serverName, authentication } = this;
    const { AUTHENTICATION_TYPES } = constants;
    let connectionStringPattern = '';
    let connectionString = undefined;

    if (authentication === AUTHENTICATION_TYPES['Standard'].value) {
      connectionStringPattern = AUTHENTICATION_TYPES['Standard'].connectionStringPattern;
    } else if (authentication === AUTHENTICATION_TYPES['Trusted'].value) {
      connectionStringPattern = AUTHENTICATION_TYPES['Trusted'].connectionStringPattern;
    }

    if (connectionStringPattern !== '') {
      connectionString = String.format(connectionStringPattern, serverName, database, login, password);
    }

    return connectionString;
  }

  /**
   * Handle ok button's click event
   */
  onOkHandle() {
    const { validation } = this.props;
    validation
      .validate()
      .then(success => {
        return success ? this.props.onOk && this.props.onOk(this.composeConnectionString()) : false;
      })
      .then(success => {
        success && this.props.onAccept();
      });
  }

  /**
   * Render connection string builder
   * @returns {Object}
   */
  renderContent(localization: Object) {
    const { authentication, serverName, database, login, options, password } = this;
    const { AUTHENTICATION_TYPES, SERVER_NAME_CONSTRAINT, DATABASE_CONSTRAINT } = constants;
    const isWindowAuthenMode = authentication == AUTHENTICATION_TYPES['Trusted'].value;

    return (
      <form>
        <div className='form-horizontal'>
          <div className='form-group'>
            <label className='control-label col-md-3 validation required'>
              {localization.getText('CONNECTION_STRING_BUILDER_LABEL_SERVER_NAME')}
            </label>
            <div className='col-md-9'>
              <InputValidator
                className='form-control'
                name='serverName'
                onChange={this.handleServerNameInput}
                value={serverName}
                type='text'
                validations={SERVER_NAME_CONSTRAINT}
                field='serverName'
              />
            </div>
          </div>
          <div className='form-group'>
            <label className='control-label col-md-3 validation required'>
              {localization.getText('CONNECTION_STRING_BUILDER_LABEL_DATABASE')}
            </label>
            <div className='col-md-9'>
              <InputValidator
                className='form-control'
                name='database'
                onChange={this.handleDatabaseInput}
                value={database}
                type='text'
                validations={DATABASE_CONSTRAINT}
                field='database'
              />
            </div>
          </div>
          <div className='form-group'>
            <label className='control-label col-md-3'>
              {localization.getText('CONNECTION_STRING_BUILDER_TEXT_AUTHENTICATION')}
            </label>
            <div className='col-md-9'>{this.buildAuthenticationOptions()}</div>
          </div>
          <div className='form-group'>
            <label className='control-label col-md-3'>{localization.getText('COMMON_LABEL_LOGIN')}</label>
            <div className='col-md-9'>
              <InputValidator
                className='form-control text-box single-line'
                name='Login'
                type='text'
                value={login}
                onChange={this.handleLoginInput}
                disabled={isWindowAuthenMode}
              />
            </div>
          </div>
          <div className='form-group'>
            <label className='control-label col-md-3'>{localization.getText('COMMON_LABEL_PASSWORD')}</label>
            <div className='col-md-9'>
              <input
                className='form-control text-box single-line'
                name='Password'
                type='password'
                value={password}
                onChange={this.handlePasswordInput}
                disabled={isWindowAuthenMode}
              />
            </div>
          </div>
          <div className='form-group'>
            <label className='control-label col-md-3'>{localization.getText('COMMON_LABEL_OPTIONS')}</label>
            <div className='col-md-9'>
              <InputValidator
                className='form-control text-box single-line'
                name='Options'
                type='text'
                value={options}
                onChange={this.handleOptionsInput}
              />
            </div>
          </div>
        </div>
      </form>
    );
  }

  /**
   * Render connection string builder modal
   * @returns {Object}
   */
  render() {
    const { localization } = this.props;

    return (
      <Popup visible onClosing={this.onClosing} type='none' autoClose={false}>
        <Popup.Header title={localization.getText('CONNECTION_STRING_BUILDER_LABEL_DATABASE_CONNECTION')} />
        <Popup.Body>{this.renderContent(localization)}</Popup.Body>
        <Popup.Footer>
          <Popup.ConfirmationButton type='accept'>{localization.getText('COMMON_LABEL_OK')}</Popup.ConfirmationButton>
          <Popup.ConfirmationButton type='cancel'>
            {localization.getText('COMMON_LABEL_CANCEL')}
          </Popup.ConfirmationButton>
        </Popup.Footer>
      </Popup>
    );
  }

  /**
   * on closing
   * @param {Object} cb
   * @param {String} type
   */

  onClosing = (cb: any, type: String) => {
    if (type === 'accept') {
      this.onOkHandle();
    } else if (type === 'cancel') {
      this.props.onReject();
    }
  };
}


// WEBPACK FOOTER //
// ./ui/share/components/controls/connectionStringBuilder/Izenda.ConnectionStringBuilder.js